// ui/toast.js
// Non-blocking toast notifications displayed in the header bar

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

const MAX_VISIBLE_TOASTS = 3;

/**
 * Toast types with their icons
 */
const TOAST_TYPES = {
    error: { icon: '❌', className: 'toast-error' },
    warning: { icon: '⚠️', className: 'toast-warning' },
    success: { icon: '✓', className: 'toast-success' },
    info: { icon: 'ℹ️', className: 'toast-info' },
};

/**
 * Get or find the toast container in the header
 */
function getContainer() {
    return document.getElementById('toast-container');
}

/**
 * Show a toast notification in the header bar
 * @param {string} message - The message to display
 * @param {string} type - Type of toast: 'error', 'warning', 'success', 'info'
 * @param {number} duration - How long to show in ms (default 2500)
 */
export function showToast(message, type = 'info', duration = 2500) {
    // Skip in non-browser environments (tests)
    if (!isBrowser) return;

    const container = getContainer();
    if (!container) return;

    const toastType = TOAST_TYPES[type] || TOAST_TYPES.info;

    const toast = document.createElement('div');
    toast.className = `toast ${toastType.className}`;
    toast.innerHTML = `
        <span class="toast-icon">${toastType.icon}</span>
        <span class="toast-message">${message}</span>
    `;

    container.appendChild(toast);

    // Enforce max visible toasts - remove oldest if exceeded
    const toasts = container.querySelectorAll('.toast');
    if (toasts.length > MAX_VISIBLE_TOASTS) {
        const oldest = toasts[0];
        if (oldest.parentNode) {
            oldest.parentNode.removeChild(oldest);
        }
    }

    // Trigger entrance animation
    requestAnimationFrame(() => {
        toast.classList.add('toast-show');
    });

    // Auto-remove after duration
    setTimeout(() => {
        toast.classList.add('toast-hide');

        // Remove from DOM after exit animation
        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 300);
    }, duration);
}

/**
 * Shorthand methods
 */
export function toastError(message, duration) {
    showToast(message, 'error', duration);
}

export function toastWarning(message, duration) {
    showToast(message, 'warning', duration);
}

export function toastSuccess(message, duration) {
    showToast(message, 'success', duration);
}

export function toastInfo(message, duration) {
    showToast(message, 'info', duration);
}

// Expose to window for easy access (browser only)
if (isBrowser) {
    window.toast = {
        show: showToast,
        error: toastError,
        warning: toastWarning,
        success: toastSuccess,
        info: toastInfo,
    };
}

export default {
    showToast,
    toastError,
    toastWarning,
    toastSuccess,
    toastInfo,
};
